import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Notification from '../components/common/Notification';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { publicAssetPath } from '../utils/publicAssetPath';
import { adminAPI } from '../services/api';

interface AdminInfo {
  id: number;
  username: string;
  email: string;
  avatar: string | null;
  role: string;
  is_active: boolean;
}

const AdminProfile: React.FC = () => {
  const navigate = useNavigate();
  const [adminInfo, setAdminInfo] = useState<AdminInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'info' | 'warning' | 'error' } | null>(null);

  // Password change form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Avatar upload
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [bookingEnabled, setBookingEnabled] = useState(true);
  const [bookingDisabledMessage, setBookingDisabledMessage] = useState('در حال حاضر امکان رزرو وجود ندارد. لطفا بعدا مراجعه کنید.');
  const [savingBookingSettings, setSavingBookingSettings] = useState(false);

  useEffect(() => {
    fetchAdminInfo();
    fetchBookingSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBookingSettings = async () => {
    try {
      const bookingResponse = await adminAPI.getBookingSettings();
      setBookingEnabled(bookingResponse.data.booking_enabled !== false);
      setBookingDisabledMessage(
        bookingResponse.data.booking_disabled_message?.trim() ||
          'در حال حاضر امکان رزرو وجود ندارد. لطفا بعدا مراجعه کنید.'
      );
    } catch (bookingErr) {
      console.error('Error fetching booking settings:', bookingErr);
    }
  };

  const handleBookingSettingsSave = async () => {
    if (adminInfo?.role !== 'admin') {
      setNotification({ message: 'فقط مدیر کل می‌تواند تنظیمات رزرو را تغییر دهد.', type: 'error' });
      return;
    }

    try {
      setSavingBookingSettings(true);
      const payload = {
        booking_enabled: bookingEnabled,
        booking_disabled_message:
          bookingDisabledMessage.trim() || 'در حال حاضر امکان رزرو وجود ندارد. لطفا بعدا مراجعه کنید.',
      };
      const response = await adminAPI.updateBookingSettings(payload);
      setBookingEnabled(response.data.booking_enabled !== false);
      setBookingDisabledMessage(response.data.booking_disabled_message);
      setNotification({ message: 'تنظیمات رزرو با موفقیت ذخیره شد.', type: 'success' });
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'خطا در ذخیره تنظیمات رزرو';
      setNotification({ message: msg, type: 'error' });
    } finally {
      setSavingBookingSettings(false);
    }
  };

  const fetchAdminInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        throw new Error('خطا در دریافت اطلاعات کاربر');
      }

      const data = await response.json();
      setAdminInfo(data);

      // نمایش عکس پروفایل فعلی
      if (data.avatar) {
        // اگر آواتار پیش‌فرض است
        if (data.avatar.startsWith('avatars/')) {
          setPreviewUrl(publicAssetPath(`/${data.avatar}`));
        } else if (data.avatar.startsWith('uploads/')) {
          setPreviewUrl(`${apiBaseUrl.replace('/api', '')}/${data.avatar}`);
        } else {
          setPreviewUrl(data.avatar);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطای ناشناخته');
    } finally {
      setLoading(false);
    }
  };

  const handleDefaultAvatarSelect = async (avatarPath: string) => {
    try {
      setError(null);

      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/auth/set-default-avatar`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ avatar_path: avatarPath }),
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در تنظیم آواتار');
      }

      setNotification({ message: 'آواتار با موفقیت تغییر یافت', type: 'success' });
      setSelectedFile(null);
      setPreviewUrl(publicAssetPath(`/${avatarPath}`));

      // به‌روزرسانی اطلاعات
      await fetchAdminInfo();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطای ناشناخته');
      setNotification({ message: err instanceof Error ? err.message : 'خطا در تنظیم آواتار', type: 'error' });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // بررسی نوع فایل
      if (!file.type.startsWith('image/')) {
        setNotification({ message: 'فقط فایل‌های تصویری مجاز است', type: 'error' });
        return;
      }

      // بررسی حجم فایل (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setNotification({ message: 'حجم فایل نباید بیشتر از 5 مگابایت باشد', type: 'error' });
        return;
      }

      setSelectedFile(file);

      // نمایش پیش‌نمایش
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = async () => {
    if (!selectedFile) {
      setNotification({ message: 'لطفاً یک فایل انتخاب کنید', type: 'warning' });
      return;
    }

    try {
      setUploadingAvatar(true);
      setError(null);

      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${apiBaseUrl}/auth/upload-avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در آپلود عکس');
      }

      await response.json(); // Response را می‌خوانیم اما داده استفاده نمی‌شود
      setNotification({ message: 'عکس پروفایل با موفقیت آپلود شد', type: 'success' });
      setSelectedFile(null);

      // به‌روزرسانی اطلاعات
      await fetchAdminInfo();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطای ناشناخته');
      setNotification({ message: err instanceof Error ? err.message : 'خطا در آپلود عکس', type: 'error' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      setNotification({ message: 'لطفاً تمام فیلدها را پر کنید', type: 'warning' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setNotification({ message: 'رمز عبور جدید و تکرار آن یکسان نیستند', type: 'error' });
      return;
    }

    if (newPassword.length < 6) {
      setNotification({ message: 'رمز عبور جدید باید حداقل 6 کاراکتر باشد', type: 'error' });
      return;
    }

    try {
      setChangingPassword(true);
      setError(null);

      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در تغییر رمز عبور');
      }

      setNotification({ message: 'رمز عبور با موفقیت تغییر یافت', type: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطای ناشناخته');
      setNotification({ message: err instanceof Error ? err.message : 'خطا در تغییر رمز عبور', type: 'error' });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error && !adminInfo) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-8">تنظیمات پروفایل</h1>

        <div className="space-y-6">
          {/* بخش اطلاعات کاربر */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">اطلاعات کاربری</h2>
            {adminInfo && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام کاربری</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{adminInfo.username}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ایمیل</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{adminInfo.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نقش</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {adminInfo.role === 'admin' ? 'مدیر اصلی' : 'کاربر'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* بخش عکس پروفایل */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">عکس پروفایل</h2>
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="پروفایل"
                    className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-200">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex-1">
                {/* آواتارهای پیش‌فرض */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">آواتارهای پیش‌فرض</label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => handleDefaultAvatarSelect('avatars/male-avatar.svg')}
                      className={`p-2 rounded-lg border-2 transition-colors ${adminInfo?.avatar === 'avatars/male-avatar.svg'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <img
                        src={publicAssetPath('/avatars/male-avatar.svg')}
                        alt="مرد"
                        className="w-16 h-16 rounded-full"
                      />
                      <p className="text-xs text-center mt-1 text-gray-600">مرد</p>
                    </button>
                    <button
                      onClick={() => handleDefaultAvatarSelect('avatars/female-avatar.svg')}
                      className={`p-2 rounded-lg border-2 transition-colors ${adminInfo?.avatar === 'avatars/female-avatar.svg'
                        ? 'border-pink-600 bg-pink-50'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <img
                        src={publicAssetPath('/avatars/female-avatar.svg')}
                        alt="زن"
                        className="w-16 h-16 rounded-full"
                      />
                      <p className="text-xs text-center mt-1 text-gray-600">زن</p>
                    </button>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">یا آپلود عکس شخصی</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="mt-2 text-xs text-gray-500">فقط فایل‌های تصویری (حداکثر 5 مگابایت)</p>
                  {selectedFile && (
                    <button
                      onClick={handleAvatarUpload}
                      disabled={uploadingAvatar}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {uploadingAvatar ? 'در حال آپلود...' : 'آپلود عکس'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {adminInfo?.role === 'admin' && (
            <div className="bg-white rounded-lg shadow p-6 border-r-4 border-amber-500">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">وضعیت صفحه رزرو عمومی</h2>
              <p className="text-sm text-gray-600 mb-4">
                هر زمان لازم بود می‌توانید رزرو را برای کاربران عمومی غیرفعال کنید تا فرم رزرو نمایش داده نشود.
              </p>
              <div className="flex items-center gap-3 mb-4">
                <input
                  id="booking-enabled"
                  type="checkbox"
                  checked={bookingEnabled}
                  onChange={(e) => setBookingEnabled(e.target.checked)}
                  disabled={savingBookingSettings}
                  className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <label htmlFor="booking-enabled" className="text-sm font-medium text-gray-800 cursor-pointer">
                  صفحه رزرو فعال باشد
                </label>
              </div>
              {!bookingEnabled && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    پیام نمایش‌داده‌شده هنگام غیرفعال بودن رزرو
                  </label>
                  <textarea
                    value={bookingDisabledMessage}
                    onChange={(e) => setBookingDisabledMessage(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="مثال: در حال حاضر امکان رزرو وجود ندارد. لطفا بعدا مراجعه کنید."
                  />
                </div>
              )}
              <button
                type="button"
                onClick={handleBookingSettingsSave}
                disabled={savingBookingSettings}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {savingBookingSettings ? 'در حال ذخیره...' : 'ذخیره تنظیمات رزرو'}
              </button>
            </div>
          )}

          {/* بخش تغییر رمز عبور */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">تغییر رمز عبور</h2>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رمز عبور فعلی
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رمز عبور جدید
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  تکرار رمز عبور جدید
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  minLength={6}
                />
              </div>
              <button
                type="submit"
                disabled={changingPassword}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {changingPassword ? 'در حال تغییر...' : 'تغییر رمز عبور'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;

